import type { Lead } from '../../../shared/types';
import { apiFetch } from './api';
import { getDb } from './db';
import { buildOp } from './outbox';
import { storeEvents } from './store';

/** A fresh server version and an idle local row are both necessary for placement. */
export const repairLocation = async (shown: Lead, pair?: { lat: number; lng: number }) => {
  const fresh = await apiFetch<Lead>('GET', `/api/leads/${shown.id}`);
  if (fresh.location_version !== shown.location_version) throw new Error('Location changed; sync and reopen before placing it.');
  const db = await getDb();
  const tx = db.transaction(['leads', 'outbox'], 'readwrite');
  const ops = await tx.objectStore('outbox').getAll();
  const local = await tx.objectStore('leads').get(shown.id);
  if (!local || local.location_version !== fresh.location_version || ops.some((op) => op.path.startsWith(`/api/leads/${shown.id}`) ||
    (op.path === '/api/leads' && (op.body as { id?: string })?.id === shown.id))) {
    await tx.done;
    throw new Error('Sync or resolve this lead’s saved edits before placing it.');
  }
  if (pair) {
    const now = new Date().toISOString();
    await tx.objectStore('leads').put({ ...local, ...pair, coordinate_source: 'manual', coordinate_at: now,
      location_status: 'resolved', location_attempted_at: null, location_version: fresh.location_version + 1 });
    await tx.objectStore('outbox').add(buildOp({ method: 'PATCH', path: `/api/leads/${shown.id}`,
      body: { ...pair, expected_location_version: fresh.location_version }, label: `Place location · ${shown.business_name}` }));
  }
  await tx.done;
  if (!pair) await apiFetch('POST', `/api/leads/${shown.id}/location/retry`, { expected_location_version: fresh.location_version });
  storeEvents.dispatchEvent(new Event('change'));
  storeEvents.dispatchEvent(new Event('write'));
};
